import * as React from 'react';
import { toast } from 'react-toastify';
import { NewJobQuoteItem, NewJobQuoteInvoiceExtra } from './models/JobInterfaces';

interface NewJobInvoiceProps {
    currentStep: number;
    siteAddress: string;
    customerName: string;
    contact: string;
    quoteItems: NewJobQuoteItem[];
    extraInvoiceItems: NewJobQuoteInvoiceExtra[];
    invoiceTotal: number;
    goPrev(): void;
    addInvoiceItem(extraInvoiceItems: NewJobQuoteInvoiceExtra): void;
    deleteInvoiceItem(deleteIndex: number): void;
    goNext(): void;
}

const NewJobInvoice = (props: NewJobInvoiceProps) => {
    const [errors, setErrors] = React.useState<{ [key: string]: string }>({});
    const [invoiceItem, setInvoiceItem] = React.useState<NewJobQuoteInvoiceExtra>({
        description: "",
        amount: 0
    });

    const clearError = (errorItem: string) => {
        let errorList = errors;
        delete errorList[errorItem];

        setErrors(errorList);
    }

    const handleChange = (e: any) => {
        e.preventDefault();

        clearError(e.target.name);

        setInvoiceItem(prevState => ({ ...prevState, [e.target.name]: e.target.value }));
    }

    const handleChangeValue = (e: any) => {
        e.preventDefault();

        clearError(e.target.name);

        setInvoiceItem(prevState => ({ ...prevState, [e.target.name]: e.target.value * 1 }));
    }

    const validate = () => {
        let errorList: any = {};
        let formIsValid = true;

        if (!invoiceItem.description || invoiceItem.description === "") {
            formIsValid = false;
            errorList["description"] = "Description is required!";
        }

        if (invoiceItem.amount <= 0) {
            formIsValid = false;
            errorList["amount"] = "Amount is required!";
        }

        setErrors(errorList);

        return formIsValid;
    }

    const addInvoiceItem = (e: any) => {
        e.preventDefault();

        if (validate()) {
            setInvoiceItem(prevState => ({ ...prevState, description: "", amount: 0, amountFormatted: "$0.00" }));
            props.addInvoiceItem(invoiceItem);
        } else {
            toast.error("Please fix errors on screen before adding invoice item!");
        }
    }

    const deleteItem = (e: any, itemIndex: number) => {
        e.preventDefault();

        props.deleteInvoiceItem(itemIndex);
    }

    const prev = () => {
        props.goPrev();
    }

    const next = () => {
        props.goNext();
    }

    return (
        <div>
            <div >
                <div>Enter any extra invoice items (if any), e.g. Damage Waiver</div>
                <div className="input-group--inline">
                    <label className="input-group" htmlFor="description">
                        <span className="label label--inline">Description</span>
                        <input className="input marginRight10" type="text" maxLength={100} id="description" name="description" value={invoiceItem.description} onChange={(e) => handleChange(e)}></input>
                    </label>
                    <span className={errors["description"] ? "label errors" : "hidden"}>{errors["description"]}</span>
                    
                    <label className="input-group" htmlFor="amount">
                        <span className="label label--inline">Amount</span>
                        <input className="input textalignright marginRight10" min={0} step={0.01} type="number" id="amount" name="amount" value={invoiceItem.amount} onChange={(e) => handleChangeValue(e)}></input>
                    </label>
                    <span className={errors["amount"] ? "label errors" : "hidden"}>{errors["amount"]}</span>
                    
                    <button type="button" className="defaultbutton defaultbutton__small" onClick={(e) => addInvoiceItem(e)}>Add</button>
                </div>

                <div className="makeitbold newJobInvoice__confirmation--size">Extra Invoice Items</div>
                
                {props.extraInvoiceItems.map((item, index) =>
                    <div key={index}>
                        <div className="input-group">
                            <span className="label-large">{item.description}</span>
                            <span className="newJobInvoice__invoiceItem marginRight10">{item.amount.toLocaleString("en-AU", { style: "currency", currency: "AUD", minimumFractionDigits: 2 })}</span>
                            <a className="makeitred" href="#" onClick={(e) => deleteItem(e, index)}>
                                <span className="fas fa-times-circle edit--icon alignIconCenter"></span>
                            </a>
                        </div>
                    </div>
                )}
                
                <hr></hr>
            </div>
            <div className="marginTop20">
                <div><b>Site Address:</b> {props.siteAddress}</div>
                <div><b>Customer:</b> {props.customerName}</div>
                <div><b>Contact:</b> {props.contact}</div>
                <hr></hr>
                <div className="makeitbold newJobInvoice__confirmation--size--large">Item Confirmation Summary</div>
                {props.quoteItems.map((item, index) => 
                    <div key={index}>
                        <div className={item.item === "" ? "hidden" : ""}>
                            <div className="input-group">
                                <span className="label-xlarge">{(item.invoiceDescription === "" ? item.item : item.invoiceDescription) + (item.frequencyId === 1 ? " (Monthly Maint)" : item.frequencyId === 2 ? " (Fortnightly Maint)" : item.frequencyId === 3 ? " (Weekly Maint)" : "") + " x " + item.quantity}</span>
                                <span className="newJobInvoice__invoiceItem">{item.initialHire === 0 ? "" : item.initialHire.toLocaleString("en-AU", { style: "currency", currency: "AUD", minimumFractionDigits: 2 })}</span>
                            </div>
                            <div className={item.travel > 0 ? "input-group" : "hidden"}>
                                <span className="label-xlarge">Travel</span>
                                <span className="newJobInvoice__invoiceItem">{item.travel.toLocaleString("en-AU", { style: "currency", currency: "AUD", minimumFractionDigits: 2 })}</span>
                            </div>
                            <div className={item.trench > 0 ? "input-group" : "hidden"}>
                                <span className="label-xlarge">{item.itemTypeId === 61 ? "Truck Hire" : "Trench Fee"}</span>
                                <span className="newJobInvoice__invoiceItem">{item.trench.toLocaleString("en-AU", { style: "currency", currency: "AUD", minimumFractionDigits: 2 })}</span>
                            </div>
                            <div className={item.confinedSpace > 0 ? "input-group" : "hidden"}>
                                <span className="label-xlarge">Confined Space</span>
                                <span className="newJobInvoice__invoiceItem">{item.confinedSpace.toLocaleString("en-AU", { style: "currency", currency: "AUD", minimumFractionDigits: 2 })}</span>
                            </div>
                            <div className={item.cleaning > 0 ? "input-group" : "hidden"}>
                                <span className="label-xlarge">{item.itemTypeId === 23 ? "Connection" : item.productGroupId === 6 ? "Callout" : "Cleaning"}</span>
                                <span className="newJobInvoice__invoiceItem">{item.cleaning.toLocaleString("en-AU", { style: "currency", currency: "AUD", minimumFractionDigits: 2 })}</span>
                            </div>
                            <div className={item.fuelSurcharge > 0 ? "input-group" : "hidden"}>
                                <span className="label-xlarge">Fuel Surcharge</span>
                                <span className="newJobInvoice__invoiceItem">{item.fuelSurcharge.toLocaleString("en-AU", { style: "currency", currency: "AUD", minimumFractionDigits: 2 })}</span>
                            </div>
                            <div className="input-group makeitbold newJobInvoice__invoiceTotal--padding">
                                <span className="newJobInvoice__invoiceTotal--label newJobInvoice__itemTotal--border">Total {item.item}</span>
                                <span className="newJobInvoice__itemTotal--border newJobInvoice__invoiceItem--total">{item.amount.toLocaleString("en-AU", { style: "currency", currency: "AUD", minimumFractionDigits: 2 })}</span>
                            </div>
                        </div>
                    </div>
                )}
                {props.extraInvoiceItems.map((invitem, index) => 
                    <div key={index}>
                        <div className="input-group makeitbold newJobInvoice__invoiceTotal--padding">
                            <span className="newJobInvoice__invoiceTotal--label newJobInvoice__itemTotal--border">{invitem.description}</span>
                            <span className="newJobInvoice__itemTotal--border newJobInvoice__invoiceItem--total">{invitem.amount.toLocaleString("en-AU", { style: "currency", currency: "AUD", minimumFractionDigits: 2 })}</span>
                        </div>
                    </div>
                )}
                <div className="input-group marginTop20">
                    <span className="makeitbold newJobInvoice__invoiceTotal newJobInvoice__invoiceTotal--label">Invoice Total (incl. GST)</span>
                    <span className="makeitbold newJobInvoice__invoiceTotal newJobInvoice__invoiceItem--total">{props.invoiceTotal.toLocaleString("en-AU", { style: "currency", currency: "AUD", minimumFractionDigits: 2 })}</span>
                </div>
                <hr></hr>
            </div>
            <div>
                <div className="input-group-parent">
                    <button className="defaultbutton" onClick={prev} type="button">Prev</button>
                </div>
                <div className="input-group-parent">
                    <div className="colAlignRight">
                        <button className="defaultbutton--save" onClick={next} type="button">Next</button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default NewJobInvoice;

//<label className="input-group" htmlFor="truck">
//    <span className="label">Truck Access?</span>
//    <select className="select" name="truckAccess" value={jobDetails.truckAccess} onChange={(e) => this.handleChange(e)}>
//        <option hidden defaultValue="-1"></option>
//        {yesNoOptions.map(yn =>
//            <option key={yn.id} value={yn.id}>{yn.name}</option>
//        )};
//    </select>
//</label>